import { IconCheck, IconChevronDown, IconClose, IconFilterList } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Clickable } from "@/components/elements/Clickable/Clickable";
import { Form } from "@/components/elements/Form";
import { Modal } from "@/components/elements/Modal";
import { SegmentedControl } from "@/components/elements/SegmentedControl";
import { DATE_NOT_TIMEZONE_FORMAT } from "@/constants/datetime";
import { START_PAGE } from "@/constants/pagination";
import { OrganizationDisplaySettingsTabContent } from "@/features/organization/components/OrganizationDisplaySettingsTabContent";
import { OrganizationFilterTabContent } from "@/features/organization/components/OrganizationFilterTabContent";
import { FILTER_ACTION, FILTER_TAB, FILTER_TAB_OPTIONS } from "@/features/organization/constants/organizationFilter";
import { organizationFilterSchema } from "@/features/organization/schema/organizationFilterSchema";
import { TFilterAction, TFilterTab, TOrganizationFilterForm } from "@/features/organization/types/organizationFilter";
import { ORG_SELECT_COLUMN_DISPLAY_ITEMS, ORG_SELECT_DEFAULT_COLUMN_ORDER } from "@/features/session/constants/organizationSelectColumns";
import { useOrganizationSelectFilterCache } from "@/features/session/providers/OrganizationSelectFilterCacheProvider";
import { useVisible } from "@/hooks/useVisible";
import { black, blue, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { dayjs } from "@/utils/dayjs";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Popover as MuiPopover, Stack, Typography, styled } from "@mui/material";
import { FC, useMemo, useRef, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { useForm } from "react-hook-form";

export const OrganizationSelectFilterPopover: FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { visible, close, open } = useVisible(false);
  const { visible: resetModalVisible, close: closeResetModal, open: openResetModal } = useVisible(false);
  const { filterCache, setFilterCache } = useOrganizationSelectFilterCache();

  const [activeTab, setActiveTab] = useState<TFilterTab>(FILTER_TAB.FILTER);

  // Build the form values from the last committed (saved) filter state.
  const buildFormDefaults = (): TOrganizationFilterForm => ({
    externalId: filterCache.externalId ?? "",
    name: filterCache.name ?? "",
    productId: filterCache.productId ?? "",
    lastLoginFrom: filterCache.lastLoginFrom ? dayjs(filterCache.lastLoginFrom) : null,
    lastLoginTo: filterCache.lastLoginTo ? dayjs(filterCache.lastLoginTo) : null,
    createdDateFrom: filterCache.createdDateFrom ? dayjs(filterCache.createdDateFrom) : null,
    createdDateTo: filterCache.createdDateTo ? dayjs(filterCache.createdDateTo) : null,
  });

  const methods = useForm<TOrganizationFilterForm>({
    resolver: yupResolver(organizationFilterSchema),
    defaultValues: buildFormDefaults(),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control, reset, watch, trigger } = methods;

  const getDefaultColumnOrder = () => (filterCache.columnOrder?.length ? filterCache.columnOrder : ORG_SELECT_DEFAULT_COLUMN_ORDER);

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(filterCache.visibleColumns ?? {});
  const [columnOrder, setColumnOrder] = useState<string[]>(getDefaultColumnOrder);

  // Discard any uncommitted edits (filter fields, column visibility/order and active tab)
  // when the popover is dismissed without saving, so it reopens with the saved settings.
  const restoreFromCache = () => {
    reset(buildFormDefaults());
    setVisibleColumns(filterCache.visibleColumns ?? {});
    setColumnOrder(getDefaultColumnOrder());
    setActiveTab(FILTER_TAB.FILTER);
  };

  const handleClose = () => {
    restoreFromCache();
    close();
  };

  const orderedColumns = columnOrder.map((key) => ORG_SELECT_COLUMN_DISPLAY_ITEMS.find((item) => item.key === key)!).filter(Boolean);

  const pendingAction = useRef<TFilterAction | null>(null);

  const filterCount = useMemo(() => {
    const conditions = [
      filterCache.externalId,
      filterCache.name,
      filterCache.productId,
      filterCache.lastLoginFrom || filterCache.lastLoginTo,
      filterCache.createdDateFrom || filterCache.createdDateTo,
    ];
    return conditions.filter(Boolean).length;
  }, [filterCache]);

  const handleToggleColumn = (key: string) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !(prev[key] ?? true) }));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, moved);
    setColumnOrder(newOrder);
  };

  const onSubmit = (formData: TOrganizationFilterForm) => {
    setFilterCache({
      ...filterCache,
      page: START_PAGE,
      externalId: formData.externalId,
      name: formData.name,
      productId: formData.productId,
      lastLoginFrom: formData.lastLoginFrom ? formData.lastLoginFrom.format(DATE_NOT_TIMEZONE_FORMAT) : null,
      lastLoginTo: formData.lastLoginTo ? formData.lastLoginTo.format(DATE_NOT_TIMEZONE_FORMAT) : null,
      createdDateFrom: formData.createdDateFrom ? formData.createdDateFrom.format(DATE_NOT_TIMEZONE_FORMAT) : null,
      createdDateTo: formData.createdDateTo ? formData.createdDateTo.format(DATE_NOT_TIMEZONE_FORMAT) : null,
      visibleColumns,
      columnOrder,
    });
    close();
  };

  const handleClear = () => {
    if (activeTab === FILTER_TAB.FILTER) {
      const values = watch();
      const hasValue = [
        values.createdDateFrom,
        values.createdDateTo,
        values.externalId,
        values.lastLoginFrom,
        values.lastLoginTo,
        values.name,
        values.productId,
      ].some(Boolean);
      if (!hasValue) return;
    } else {
      const isDefaultOrder =
        columnOrder.length === ORG_SELECT_DEFAULT_COLUMN_ORDER.length && columnOrder.every((key, i) => key === ORG_SELECT_DEFAULT_COLUMN_ORDER[i]);
      const isAllVisible = ORG_SELECT_COLUMN_DISPLAY_ITEMS.every((item) => visibleColumns[item.key] !== false);
      if (isDefaultOrder && isAllVisible) return;
    }
    pendingAction.current = FILTER_ACTION.CLEAR;
    openResetModal();
  };

  const handleChangeTab = (tab: string) => {
    setActiveTab(tab as TFilterTab);
  };

  const handleResetFilter = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    pendingAction.current = FILTER_ACTION.RESET;
    openResetModal();
  };

  const handleConfirmReset = () => {
    if (pendingAction.current === FILTER_ACTION.RESET) {
      reset({ createdDateFrom: null, createdDateTo: null, externalId: "", lastLoginFrom: null, lastLoginTo: null, name: "", productId: "" });
      setFilterCache({
        ...filterCache,
        page: START_PAGE,
        externalId: "",
        name: "",
        productId: "",
        lastLoginFrom: null,
        lastLoginTo: null,
        createdDateFrom: null,
        createdDateTo: null,
      });
    } else if (pendingAction.current === FILTER_ACTION.CLEAR) {
      if (activeTab === FILTER_TAB.FILTER) {
        reset({ createdDateFrom: null, createdDateTo: null, externalId: "", lastLoginFrom: null, lastLoginTo: null, name: "", productId: "" });
      } else {
        setVisibleColumns(Object.fromEntries(ORG_SELECT_COLUMN_DISPLAY_ITEMS.map((item) => [item.key, true])));
        setColumnOrder(ORG_SELECT_DEFAULT_COLUMN_ORDER);
      }
    }
    pendingAction.current = null;
    closeResetModal();
  };

  return (
    <>
      <Clickable
        onClick={open}
        ref={ref}
        sx={{
          "& > div:first-of-type": {
            border: `1px solid ${filterCount > 0 ? blue[80] : black[95]}`,
            padding: filterCount > 0 ? "3px 8px" : "4px 8px",
          },
        }}
        buttonWidth={filterCount > 0 ? "calc(100% - 28px)" : "100%"}
      >
        <AnchorWrapper flexDirection="row" alignItems="center" gap={1}>
          <IconFilterList />
          <Stack flexDirection="row" alignItems="center">
            <Typography variant="cap12Bold">{filterCount === 0 ? "フィルタ" : "フィルタ適用数"}</Typography>
            {filterCount > 0 && (
              <Stack flexDirection="row" alignItems="center" gap="4px">
                <Typography variant="cap12Bold">:</Typography>
                <Typography variant="cap12Bold" color={blue[80]}>
                  {filterCount}
                </Typography>
              </Stack>
            )}
          </Stack>
          {filterCount > 0 ? (
            <Stack onClick={handleResetFilter} sx={{ zIndex: 9999, cursor: "pointer" }}>
              <IconClose fontSize={20} color={black[80]} />
            </Stack>
          ) : (
            <IconChevronDown style={visible ? { transform: "rotate(180deg)" } : undefined} />
          )}
        </AnchorWrapper>
      </Clickable>
      <MuiPopover
        open={visible}
        anchorEl={ref.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          ".MuiPaper-root": {
            padding: 0,
            borderRadius: "16px",
          },
        }}
      >
        <Form methods={methods} onSubmit={onSubmit} schema={organizationFilterSchema}>
          <Box width={780}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" px={3} pt={2.5} pb={1.5}>
              <Typography variant="sub16Semi" color={text.primary}>
                {activeTab === FILTER_TAB.FILTER ? "フィルタ" : "表示項目設定"}
              </Typography>
              <SegmentedControl value={activeTab} options={FILTER_TAB_OPTIONS} onChange={handleChangeTab} />
            </Stack>

            {activeTab === FILTER_TAB.FILTER && <OrganizationFilterTabContent control={control} trigger={trigger} />}

            {activeTab === FILTER_TAB.DISPLAY_SETTINGS && (
              <OrganizationDisplaySettingsTabContent
                columns={orderedColumns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onDragEnd={handleDragEnd}
              />
            )}

            <Divider />
            <Stack flexDirection="row" justifyContent="space-between" px={3} py={2}>
              <Stack flexDirection="row" gap={2} alignItems="center">
                <Button type="button" variant="ghost" size="sm" onClick={handleClose}>
                  キャンセル
                </Button>
                <Typography variant="body14" color={text.secondary} sx={{ cursor: "pointer", "&:hover": { opacity: 0.7 } }} onClick={handleClear}>
                  クリア
                </Typography>
              </Stack>
              <Button size="sm" type="submit" sx={{ gap: 1 }}>
                <IconCheck />
                条件を設定
              </Button>
            </Stack>
          </Box>
        </Form>
      </MuiPopover>
      <Modal
        isOpen={resetModalVisible}
        onClose={closeResetModal}
        onOk={handleConfirmReset}
        labelClose="いいえ"
        labelOk="はい"
        width={400}
        title={<Typography variant="body14Bold">設定した条件がすべてクリアされますがよろしいですか？</Typography>}
      />
    </>
  );
};

const AnchorWrapper = styled(Stack)`
  max-width: 180px;
  border: 1px solid ${divider.middle};
  border-radius: ${rounded.sm};
  padding: 4px 8px;
  &:hover {
    cursor: pointer;
    opacity: 0.6;
  }
`;
